/**
 * Central route & slug mapping between Danish and English pages.
 * Ensures consistent URLs, bidirectional language switching (DK ↔ EN),
 * self-canonicals and hreflang alternates.
 */

export const ROUTE_MAP_DA_TO_EN = {
  '/': '/en',
  '/udredning': '/en/assessment',
  '/adhd': '/en/adhd',
  '/autisme': '/en/autism',
  '/om-os': '/en/about-us',
  '/psykiatriske-lidelser': '/en/psychiatric-conditions',
  '/psykiatriske-lidelser/angst': '/en/psychiatric-conditions/anxiety',
  '/psykiatriske-lidelser/ocd': '/en/psychiatric-conditions/ocd',
  '/psykiatriske-lidelser/depression': '/en/psychiatric-conditions/depression',
  '/psykiatriske-lidelser/ptsd': '/en/psychiatric-conditions/ptsd',
  '/psykiatriske-lidelser/personlighedsforstyrrelser': '/en/psychiatric-conditions/personality-disorders',
  '/vidensbank': '/en/knowledge-base',
  '/vidensbank/adhd-screening': '/en/knowledge-base/adhd-screening',
  '/vidensbank/autisme-screening': '/en/knowledge-base/autism-screening',
  '/vidensbank/mistanke-om-adhd': '/en/knowledge-base/suspected-adhd',
  '/priser': '/en/prices',
  '/afbud-og-betaling': '/en/cancellation-and-payment',
  '/afbud': '/en/cancellation',
  '/klagevejledning': '/en/complaints-procedure',
  '/akut-hjaelp': '/en/emergency-help',
  '/privatlivspolitik': '/en/privacy-policy',
  '/cookiepolitik': '/en/cookie-policy',
  '/kontakt': '/en/contact',
  '/medicinsk-behandling': '/en/medical-treatment',
  '/403': '/en/403'
};

// Build reverse map (EN path -> DA path)
export const ROUTE_MAP_EN_TO_DA = {};
Object.entries(ROUTE_MAP_DA_TO_EN).forEach(([da, en]) => {
  ROUTE_MAP_EN_TO_DA[en] = da;
});

// Also include legacy /en/da-slug paths as aliases in the reverse map
Object.entries(ROUTE_MAP_DA_TO_EN).forEach(([da]) => {
  if (da !== '/') {
    ROUTE_MAP_EN_TO_DA[`/en${da}`] = da;
  }
});

/**
 * Normalise a path by removing trailing slash (except for single slash)
 */
export function normalizePath(path) {
  if (!path || typeof path !== 'string') return '/';
  const clean = path.split('?')[0].split('#')[0];
  if (clean === '' || clean === '/') return '/';
  return clean.replace(/\/+$/, '');
}

/**
 * Convert any path to its proper English equivalent
 */
export function toEnglishPath(path) {
  if (!path || typeof path !== 'string') return '/en';
  if (
    path.startsWith('http://') ||
    path.startsWith('https://') ||
    path.startsWith('mailto:') ||
    path.startsWith('tel:') ||
    path.startsWith('#')
  ) {
    return path;
  }

  const clean = normalizePath(path);

  // Exact match in mapping
  if (ROUTE_MAP_DA_TO_EN[clean]) {
    return ROUTE_MAP_DA_TO_EN[clean];
  }

  // Already an English path mapped in reverse
  if (ROUTE_MAP_EN_TO_DA[clean]) {
    // If it's a legacy /en/da-slug, translate to proper English slug
    const da = ROUTE_MAP_EN_TO_DA[clean];
    return ROUTE_MAP_DA_TO_EN[da] || clean;
  }

  // Dynamic /vidensbank/:slug article link
  if (clean.startsWith('/vidensbank/')) {
    const slug = clean.replace('/vidensbank/', '');
    return `/en/knowledge-base/${slug}`;
  }

  // Legacy /en/vidensbank/:slug link
  if (clean.startsWith('/en/vidensbank/')) {
    const slug = clean.replace('/en/vidensbank/', '');
    return `/en/knowledge-base/${slug}`;
  }

  // Already starts with /en
  if (clean === '/en' || clean.startsWith('/en/')) {
    return clean;
  }

  // Fallback
  return `/en${clean.startsWith('/') ? '' : '/'}${clean}`;
}

/**
 * Convert any path to its proper Danish equivalent
 */
export function toDanishPath(path) {
  if (!path || typeof path !== 'string') return '/';
  if (
    path.startsWith('http://') ||
    path.startsWith('https://') ||
    path.startsWith('mailto:') ||
    path.startsWith('tel:') ||
    path.startsWith('#')
  ) {
    return path;
  }

  const clean = normalizePath(path);

  // If already Danish
  if (ROUTE_MAP_DA_TO_EN[clean] && !clean.startsWith('/en')) {
    return clean;
  }

  // In reverse map
  if (ROUTE_MAP_EN_TO_DA[clean]) {
    return ROUTE_MAP_EN_TO_DA[clean];
  }

  // Dynamic /en/knowledge-base/:slug article link
  if (clean.startsWith('/en/knowledge-base/')) {
    const slug = clean.replace('/en/knowledge-base/', '');
    return `/vidensbank/${slug}`;
  }

  // Legacy /en/vidensbank/:slug link
  if (clean.startsWith('/en/vidensbank/')) {
    const slug = clean.replace('/en/vidensbank/', '');
    return `/vidensbank/${slug}`;
  }

  // Strip leading /en
  if (clean === '/en') return '/';
  if (clean.startsWith('/en/')) {
    return clean.replace(/^\/en/, '') || '/';
  }

  return clean;
}

/**
 * Localize a path according to the current active language
 */
export function localizePath(path, lang) {
  return lang === 'en' || lang === 'sv' ? toEnglishPath(path) : toDanishPath(path);
}
